import { useCallback } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "@/app/hooks";
import { useBoardData } from "./useBoardData";
import { useBoardFilters } from "./useBoardFilters";
import {
  selectApiData,
  selectGroupingConfig,
  selectDebouncedSearchQuery,
  selectColumns,
  selectSubGroupData,
  selectGroupingDataMemoized,
  selectSubGroupingDataMemoized,
  selectActiveSprintIds,
  selectBoardState,
  selectSelectedItem,
} from "@/features/bughurd/boardSelector";
import {
  setLoadingStatus,
  setPagination,
  setHasMore,
  updateColumn,
  setSubGroupColumnData,
  setSubGroupLoadingState,
} from "@/features/bughurd/slice";

export const useBoardFetch = () => {
  const { pathname } = useLocation();
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const appGlobal = useAppSelector((state) => state.appGlobal);
  const localUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const currentUser = appGlobal.currentUser || localUser;
  const isMyTaskView = pathname.includes("/defect-tracker/my-tasks");
  const {
    getBugsByStatus,
    fetchInProgress,
    previousTasksRef,
    preserveScrollPosition,
    restoreScrollPosition,
    isDragInProgress,
    PAGE_LIMIT,
  } = useBoardData();

  const {
    selectedTags,
    selectedAvatars,
    selectedStatuses,
    selectedPriorities,
    selectedProjects,
    dueDateStart,
    dueDateEnd,
    startDateStart,
    startDateEnd,
    updatedAtStart,
    updatedAtEnd,
    isRecurring,
    selectedCreatedBy,
  } = useBoardFilters();
  const selectedItem = useAppSelector(selectSelectedItem);
  const apiData = useAppSelector(selectApiData);
  const { statusList, tagList, projects } = apiData;
  const groupingConfig = useAppSelector(selectGroupingConfig);
  const { sortOrder } = groupingConfig;
  const debouncedSearchQuery = useAppSelector(selectDebouncedSearchQuery);
  const columns = useAppSelector(selectColumns);
  const subGroupData = useAppSelector(selectSubGroupData);
  const activeSprintIds = useAppSelector(selectActiveSprintIds);
  const boardState = useAppSelector(selectBoardState);
  const { projectDetails } = boardState;
  const selectedSprintData = boardState?.selectedSprintData;
  const boardType = projectDetails?.boardType;
  // Get filter value based on group type
  const getFilterValueForGroup = useCallback(
    (groupId: string, groupData: any) => {
      const isMyTaskView = pathname.includes("/defect-tracker/my-tasks");

      switch (groupData.type) {
        case "status":
          if (isMyTaskView) {
            // For My Tasks view, find the status object and use its statusIds array
            const statusObject = statusList?.find(
              (status) => status._id === groupId
            );
            if (
              statusObject &&
              statusObject.statusIds &&
              statusObject.statusIds.length > 0
            ) {
              return { statuses: statusObject.statusIds };
            }
            // Fallback to single status ID if statusIds array is not available
            return { statuses: [groupId] };
          } else {
            // For regular project view, pass status ID as before
            return { statuses: [groupId] };
          }
        case "assignee":
          return { assignTo: [groupId] };
        case "priority":
          return { severities: [groupData.title] };
        case "tag":
          return { tags: [groupData.tagName] };
        case "taskType":
          return { type: groupData.title };
        case "dueDate":
          return getDueDateFilter(groupId);
        case "startDate":
          return getStartDateFilter(groupId);
        case "project":
          return { projects: [groupId] };
        //is recurring
        case "recurring":
          return { isRecurring: true };
        default:
          return {};
      }
    },
    [statusList]
  );

  // Get filter value for sub-group
  const getFilterValueForSubGroup = useCallback(
    (subGroupId: string, subGroupData: any) => {
      const isMyTaskView = pathname.includes("/defect-tracker/my-tasks");

      switch (subGroupData.type) {
        case "status":
          if (isMyTaskView) {
            // For My Tasks view, find the status object and use its statusIds array
            const statusObject = statusList?.find(
              (status) => status._id === subGroupId
            );
            if (
              statusObject &&
              statusObject.statusIds &&
              statusObject.statusIds.length > 0
            ) {
              return { statuses: statusObject.statusIds };
            }
            // Fallback to single status ID if statusIds array is not available
            return { statuses: [subGroupId] };
          } else {
            // For regular project view, pass status ID as before
            return { statuses: [subGroupId] };
          }
        case "assignee":
          return { assignTo: [subGroupId] };
        case "priority":
          return { severities: [subGroupData.title] };
        case "tag":
          return { tags: [subGroupData.tagName] };
        case "taskType":
          return { type: subGroupData.title };
        case "dueDate":
          return getDueDateFilter(subGroupId);
        case "startDate":
          return getStartDateFilter(subGroupId);
        case "project":
          return { projects: [subGroupId] };
        //is recurring
        case "recurring":
          return { isRecurring: true };
        default:
          return {};
      }
    },
    [statusList]
  );

  // Get due date filter based on group
  const getDueDateFilter = useCallback((groupId: string) => {
    const today = new Date();
    const startOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    const endOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      23,
      59,
      59
    );

    switch (groupId) {
      case "overdue":
        return { dueDateEnd: startOfToday.toISOString() };
      case "today":
        return {
          dueDateStart: startOfToday.toISOString(),
          dueDateEnd: endOfToday.toISOString(),
        };
      case "this_week":
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay() + 1);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return {
          dueDateStart: startOfWeek.toISOString(),
          dueDateEnd: endOfWeek.toISOString(),
        };
      case "next_week":
        const nextWeekStart = new Date(today);
        nextWeekStart.setDate(today.getDate() + 7 - today.getDay());
        const nextWeekEnd = new Date(nextWeekStart);
        nextWeekEnd.setDate(nextWeekStart.getDate() + 6);
        return {
          dueDateStart: nextWeekStart.toISOString(),
          dueDateEnd: nextWeekEnd.toISOString(),
        };
      case "later":
        const twoWeeksFromNow = new Date(today);
        twoWeeksFromNow.setDate(today.getDate() + 14);
        return { dueDateStart: twoWeeksFromNow.toISOString() };
      case "no_due_date":
        return { filterBy: "dueDate", filterValue: "null" };
      default:
        return {};
    }
  }, []);

  // Get start date filter based on group - Add this function
  const getStartDateFilter = useCallback((groupId: string) => {
    const today = new Date();
    const startOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    const endOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      23,
      59,
      59
    );

    switch (groupId) {
      case "overdue_start":
        return { startDateEnd: startOfToday.toISOString() };
      case "today_start":
        return {
          startDateStart: startOfToday.toISOString(),
          startDateEnd: endOfToday.toISOString(),
        };
      case "this_week_start":
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay() + 1);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return {
          startDateStart: startOfWeek.toISOString(),
          startDateEnd: endOfWeek.toISOString(),
        };
      case "next_week_start":
        const nextWeekStart = new Date(today);
        nextWeekStart.setDate(today.getDate() + 7 - today.getDay());
        const nextWeekEnd = new Date(nextWeekStart);
        nextWeekEnd.setDate(nextWeekStart.getDate() + 6);
        return {
          startDateStart: nextWeekStart.toISOString(),
          startDateEnd: nextWeekEnd.toISOString(),
        };
      case "later_start":
        const twoWeeksFromNow = new Date(today);
        twoWeeksFromNow.setDate(today.getDate() + 14);
        return { startDateStart: twoWeeksFromNow.toISOString() };
      case "no_start_date":
        return { filterBy: "startDate", filterValue: "null" };
      default:
        return {};
    }
  }, []);

  // Helper function to get filtered projects for MyTaskView
  const getFilteredProjects = useCallback(() => {
    // if (!isMyTaskView) return undefined;

    // If there are selected projects in filters, use only those
    if (isMyTaskView &&selectedProjects && selectedProjects.length > 0) {
      return selectedProjects;
    }
    return undefined; 

    // Otherwise, use all available projects
    // return projects?.map((item) => item._id);
  }, [isMyTaskView, selectedProjects, projects]);

  // Enhanced fetchBugsForGroup to handle subgroups properly
  const fetchBugsForGroup = useCallback(
    async (
      groupId: string,
      groupData: any,
      page = 1,
      limit = PAGE_LIMIT,
      append = false,
      forceFetch = false,
      subGroupId?: string,
      subGroupDataInfo?: any
    ) => {
      if (isDragInProgress.current && !forceFetch) {
        return;
      }

      const fetchKey = subGroupId ? `${subGroupId}-${groupId}` : groupId;

      if (fetchInProgress.current[fetchKey] && !append) {
        return;
      }

      try {
        fetchInProgress.current[fetchKey] = true;

        // Set loading state before API call
        if (subGroupId) {
          dispatch(
            setSubGroupLoadingState({
              subGroupId,
              columnId: groupId,
              loadingState: {
                isInitialLoading: !append && page === 1,
                isLoadingMore: append || page > 1,
              },
            })
          );
        } else {
          dispatch(
            setLoadingStatus({
              key: groupId,
              loading: {
                isInitialLoading: !append && page === 1,
                isLoadingMore: append || page > 1,
              },
            })
          );
        }

        if (!append) {
          preserveScrollPosition(groupId);
        }

        // Store current task count for append operations
        let currentTaskCount = 0;
        if (append) {
          currentTaskCount = subGroupId
            ? subGroupData[subGroupId]?.columns?.[groupId]?.tasks?.length || 0
            : columns[groupId]?.tasks?.length || 0;
          previousTasksRef.current[fetchKey] = currentTaskCount;
        }

        const groupFilters = getFilterValueForGroup(groupId, groupData);
        const subGroupFilters =
          subGroupId && subGroupDataInfo
            ? getFilterValueForSubGroup(subGroupId, subGroupDataInfo)
            : {};

        if (subGroupId) {
        }

        let effectiveLimit = limit || PAGE_LIMIT;
        const queryParams: any = {
          id: isMyTaskView ? "" : id || "",
          forBoard: true,
          search: debouncedSearchQuery || "",
          page,
          limit: effectiveLimit,
          timestamp: new Date().getTime(),
          sortBy: "createdAt",
          sortType: sortOrder === "Ascending" ? "ASC" : "DESC",
        };

        // Handle project filtering
        if (isMyTaskView) {
          const filteredProjects = getFilteredProjects();
          if (filteredProjects && filteredProjects.length > 0) {
            queryParams.project = filteredProjects;
          }
        } else {
          // For regular project view, if project filter is selected and we're not grouping by project
          if (
            selectedProjects &&
            selectedProjects.length > 0 &&
            groupData.type !== "project" &&
            subGroupDataInfo?.type !== "project"
          ) {
            queryParams.project = selectedProjects;
          }
        }
        const isDateBasedGrouping =
          groupData.type === "dueDate" ||
          groupData.type === "startDate" ||
          subGroupDataInfo?.type === "dueDate" ||
          subGroupDataInfo?.type === "startDate";

        if (!isDateBasedGrouping) {
          // Apply global due date filters only if not grouping by date
          if (dueDateStart !== undefined) {
            queryParams.dueDateStart = dueDateStart;
          }
          if (dueDateEnd !== undefined) {
            queryParams.dueDateEnd = dueDateEnd;
          }

          // Apply global start date filters only if not grouping by date
          if (startDateStart !== undefined) {
            queryParams.startDateStart = startDateStart;
          }
          if (startDateEnd !== undefined) {
            queryParams.startDateEnd = startDateEnd;
          }
        }

        // Always apply updated date filters (independent of grouping)
        if (updatedAtStart) {
          queryParams.updatedAtStart = updatedAtStart;
        }
        if (updatedAtEnd) {
          queryParams.updatedAtEnd = updatedAtEnd;
        }
        if (isRecurring) {
          queryParams.isRecurring = isRecurring;
        }
        if (selectedCreatedBy) {
          queryParams.createdBy = selectedCreatedBy;
        }

        // Apply filtering logic for user selections
        if (isMyTaskView && currentUser?._id) {
          if (groupData.type === "assignee") {
            queryParams.assignTo = [groupId];
          } else if (subGroupDataInfo?.type === "assignee") {
            queryParams.assignTo = [subGroupId];
          } else {
            if (selectedAvatars?.length > 0) {
              queryParams.assignTo = selectedAvatars;
            } else {
              queryParams.assignTo = [currentUser._id];
            }
          }
        } else {
          if (
            groupData.type !== "assignee" &&
            subGroupDataInfo?.type !== "assignee" &&
            selectedAvatars?.length > 0
          ) {
            queryParams.assignTo = selectedAvatars;
          }
        }

        if (
          groupData.type !== "tag" &&
          subGroupDataInfo?.type !== "tag" &&
          selectedTags?.length > 0
        ) {
          if (tagList?.length > 0) {
            const tagNames = selectedTags
              .map((tagId) => {
                const tag = tagList.find((t: any) => t.tagName === tagId);
                return tag ? tag.tagName : null;
              })
              .filter(Boolean);
            if (tagNames.length > 0) {
              queryParams.tags = tagNames;
            }
          }
        }

        if (
          groupData.type !== "priority" &&
          subGroupDataInfo?.type !== "priority" &&
          selectedPriorities?.length > 0
        ) {
          queryParams.severities = selectedPriorities;
        }

        if (
          groupData.type !== "status" &&
          subGroupDataInfo?.type !== "status" &&
          selectedStatuses?.length > 0
        ) {
          queryParams.statuses = selectedStatuses;
        }

        if (!isMyTaskView && boardType === "SCRUM") {
          if (selectedSprintData && selectedItem !== "All Sprints") {
            queryParams.sprint = [selectedSprintData?._id];
          } else {
            queryParams.sprint = activeSprintIds;
          }
        } else {
          queryParams.showEpic = true;
        }

        // Apply group-specific filters AFTER global filters
        // Handle group filters carefully to not override global date filters
        Object.keys(groupFilters).forEach((key) => {
          if (key === "dueDateStart" || key === "dueDateEnd") {
            // Only apply group date filters if no global date filters are set AND it's a date-based grouping
            if (
              !queryParams[key] &&
              (groupData.type === "dueDate" ||
                subGroupDataInfo?.type === "dueDate")
            ) {
              queryParams[key] = groupFilters[key];
            }
          } else if (key === "startDateStart" || key === "startDateEnd") {
            // Only apply group start date filters if no global start date filters are set AND it's a start date-based grouping
            if (
              !queryParams[key] &&
              (groupData.type === "startDate" ||
                subGroupDataInfo?.type === "startDate")
            ) {
              queryParams[key] = groupFilters[key];
            }
          }
          //  else if (key === "projects") {
          //   // Handle project filters - only apply group project filter if no global project filter is set
          //   if (!queryParams[key] && groupData.type === "project") {
          //     queryParams[key] = groupFilters[key];
          //   }
          // }
          else {
            // Apply all other non-date group filters
            queryParams[key] = groupFilters[key];
          }
        });

        // Handle sub-group filters
        Object.keys(subGroupFilters).forEach((key) => {
          if (key === "dueDateStart" || key === "dueDateEnd") {
            // Only apply sub-group date filters if no global date filters are set AND it's a date-based sub-grouping
            if (!queryParams[key] && subGroupDataInfo?.type === "dueDate") {
              queryParams[key] = subGroupFilters[key];
            }
          } else if (key === "startDateStart" || key === "startDateEnd") {
            // Only apply sub-group start date filters if no global start date filters are set AND it's a start date-based sub-grouping
            if (!queryParams[key] && subGroupDataInfo?.type === "startDate") {
              queryParams[key] = subGroupFilters[key];
            }
          }
          // else if (key === "projects") {
          //   // Handle sub-group project filters - only apply if no global project filter is set
          //   if (!queryParams[key] && subGroupDataInfo?.type === "project") {
          //     queryParams[key] = subGroupFilters[key];
          //   }
          // }
          else {
            // Apply all other non-date sub-group filters
            queryParams[key] = subGroupFilters[key];
          }
        });

        const result = await getBugsByStatus(queryParams).unwrap();
        const bugs = result?.data?.decryptedData?.items || [];
        const total = result?.data?.decryptedData?.total || 0;
        const receivedItemsCount = bugs.length;

        if (subGroupId) {
          // Handle sub-group data update
          const currentTasks = append
            ? subGroupData[subGroupId]?.columns?.[groupId]?.tasks || []
            : [];

          const finalTasks = append ? [...currentTasks, ...bugs] : bugs;

          // Calculate hasMore more accurately
          const totalTasksAfterUpdate = finalTasks.length;
          const hasMore =
            totalTasksAfterUpdate < total && receivedItemsCount === limit;

          dispatch(
            setSubGroupColumnData({
              subGroupId,
              columnId: groupId,
              tasks: finalTasks,
              totalCount: total,
              pagination: {
                page,
                limit,
                total,
              },
              hasMore,
              loadingState: {
                isInitialLoading: false,
                isLoadingMore: false,
              },
              append,
            })
          );
        } else {
          // Regular column update logic
          const finalTasks =
            append && page > 1
              ? [...(columns[groupId]?.tasks || []), ...bugs]
              : bugs;

          const totalTasksAfterUpdate = finalTasks.length;
          const hasMore =
            totalTasksAfterUpdate < total && receivedItemsCount === limit;

          dispatch(
            setPagination({
              key: groupId,
              pagination: {
                page,
                limit,
                total,
              },
            })
          );

          dispatch(
            setHasMore({
              key: groupId,
              hasMore,
            })
          );

          const updatedColumn = {
            ...columns[groupId],
            tasks: finalTasks,
            totalCount: total,
            description:
              columns[groupId]?.description || groupData.description || "",
            hidden: columns[groupId]?.hidden || groupData.hidden || false,
          };

          dispatch(
            updateColumn({
              columnId: groupId,
              column: updatedColumn,
            })
          );

          dispatch(
            setLoadingStatus({
              key: groupId,
              loading: {
                isInitialLoading: false,
                isLoadingMore: false,
              },
            })
          );
        }

        if (!append) {
          // Restore scroll position using the scrollPositions from state
          const restoreScroll = restoreScrollPosition({});
          restoreScroll(groupId);
        }
      } catch (error) {
        console.error(`❌ Error fetching bugs for ${fetchKey}:`, error);

        if (subGroupId) {
          dispatch(
            setSubGroupLoadingState({
              subGroupId,
              columnId: groupId,
              loadingState: {
                isInitialLoading: false,
                isLoadingMore: false,
              },
            })
          );
        } else {
          dispatch(
            setLoadingStatus({
              key: groupId,
              loading: {
                isInitialLoading: false,
                isLoadingMore: false,
              },
            })
          );

          dispatch(
            setHasMore({
              key: groupId,
              hasMore: false,
            })
          );
        }
      } finally {
        fetchInProgress.current[fetchKey] = false;
      }
    },
    [
      id,
      currentUser,
      debouncedSearchQuery,
      selectedAvatars,
      selectedTags,
      selectedProjects,
      getBugsByStatus,
      columns,
      subGroupData,
      preserveScrollPosition,
      restoreScrollPosition,
      selectedPriorities,
      sortOrder,
      dueDateStart,
      dueDateEnd,
      startDateStart,
      isRecurring,
      startDateEnd,
      updatedAtStart,
      updatedAtEnd,
      selectedCreatedBy,
      selectedStatuses,
      tagList,
      projects,
      getFilterValueForGroup,
      getFilterValueForSubGroup,
      getFilteredProjects,
      statusList,
      activeSprintIds,
      projectDetails,
      selectedSprintData,
    ]
  );

  return {
    fetchBugsForGroup,
    getFilterValueForGroup,
    getFilterValueForSubGroup,
    getDueDateFilter,
    getStartDateFilter,
  };
};
